import { WallCornerMap, WallShapeType, WallEdgeConnection, JointPolygonData, OutlineSegmentCollection } from '../types/floorplannerTypes';
import { JointInfo } from './JointDetector';
import { renderStore } from '../store/renderStore';
import type { EditorStoreType } from '../store/editorStore';
import type { FloorplannerStore } from '../store/floorplannerStore';
import { SimplifiedWallGeometry, CachedJointPolygon, CachedOutlineSegments } from '../types/wallCacheTypes';
import * as THREE from 'three';

// Lazy accessor to avoid circular dependency with floorplannerStore
let _floorplannerStore: FloorplannerStore | null = null;
const getFloorplannerStore = (): FloorplannerStore => {
  if (!_floorplannerStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniFloorplannerStore?: FloorplannerStore }).__boniFloorplannerStore;
    if (globalStore) {
      _floorplannerStore = globalStore;
    } else {
      throw new Error('FloorplannerStore not yet initialized - WallGeometryCache called too early');
    }
  }
  return _floorplannerStore;
};

// Lazy accessor to avoid circular dependency with editorStore
let _editorStore: EditorStoreType | null = null;
const getEditorStore = (): EditorStoreType | null => {
  if (!_editorStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniEditorStore?: EditorStoreType }).__boniEditorStore;
    if (globalStore) {
      _editorStore = globalStore;
    }
    // Return null if not available - cache will be enabled by default
  }
  return _editorStore;
};

/**
 * Check if the cache is disabled via debug flag.
 * When disabled, all cache operations return null/empty to force recalculation.
 */
function isCacheDisabled(): boolean {
  const editor = getEditorStore();
  // If editorStore is not yet available, assume cache is enabled
  return editor?.debugDisableWallGeometryCache ?? false;
}

export interface WallGeometry {
  inner: WallShapeType[];
  outline1: WallShapeType[];
  outline2: WallShapeType[];
  endcaps: WallShapeType[];
  geometryVersion?: number; // Version of the wall geometry when this was cached
  dependencyVersions?: Map<string, number>; // Versions of connected walls when this was cached
}

export class WallGeometryCache {
  private geometryCache: Map<string, WallGeometry>;
  private jointToWallsMap: Map<string, Set<string>>;
  private dirtyWalls: Set<string>;
  // New simplified cache types
  private simplifiedGeometryCache: Map<string, SimplifiedWallGeometry>;
  private jointPolygonCache: Map<string, CachedJointPolygon>;
  private outlineSegmentCache: Map<string, CachedOutlineSegments>;

  constructor() {
    this.geometryCache = new Map();
    this.jointToWallsMap = new Map();
    this.dirtyWalls = new Set();
    // Initialize new cache types
    this.simplifiedGeometryCache = new Map();
    this.jointPolygonCache = new Map();
    this.outlineSegmentCache = new Map();
  }

  setWallGeometry(wallId: string, geometry: Partial<WallGeometry>, wall?: any, floorplannerStore?: any): void {
    // If cache is disabled, don't store anything
    if (isCacheDisabled()) {
      return;
    }
    // Always use simplified endpoint-based geometry caching
    // Delegate to simplified geometry caching
    this.setSimplifiedWallGeometry(wallId, wall, floorplannerStore);
  }

  getWallGeometry(wallId: string): WallGeometry | null {
    // If cache is disabled, always return null to force recalculation
    if (isCacheDisabled()) {
      return null;
    }
    const geometry = this.geometryCache.get(wallId);
    if (!geometry) {
      return null;
    }
    // Return a deep clone to prevent external modifications
    return this.cloneWallGeometry(geometry);
  }

  /**
   * Cache simplified endpoint-based wall geometry.
   */
  setSimplifiedWallGeometry(wallId: string, wall: any, floorplannerStore: any): void {
    // If cache is disabled, don't store anything
    if (isCacheDisabled()) {
      return;
    }
    if (!wall) return;

    // Create simplified geometry with endpoint positions
    const simplified: SimplifiedWallGeometry = {
      wallId,
      start: wall.start.clone(),
      end: wall.end.clone(),
      width: wall.wallWidth || 0,
      geometryVersion: wall.geometryVersion || 0,
      dependencyVersions: new Map()
    };

    // Capture center connection versions (legacy)
    if (wall.centerConnections && floorplannerStore) {
      for (const connection of wall.centerConnections) {
        const connectedWall = getFloorplannerStore().wallsMap.get(connection.id);
        if (connectedWall) {
          simplified.dependencyVersions.set(connection.id, connectedWall.geometryVersion || 0);
        }
      }
    }

    // Capture edge connection versions (new)
    if (wall.edgeConnections) {
      simplified.edgeConnectionVersions = new Map();
      for (const edgeConn of wall.edgeConnections) {
        const connectedWall = getFloorplannerStore().wallsMap.get(edgeConn.targetWallId);
        if (connectedWall) {
          // Use composite key including endpoints to preserve multiplicity
          const compositeKey = `${edgeConn.targetWallId}:${edgeConn.sourceEndpoint}:${edgeConn.targetEndpoint}`;
          simplified.edgeConnectionVersions!.set(compositeKey, connectedWall.geometryVersion || 0);
        }
      }
    }

    this.simplifiedGeometryCache.set(wallId, simplified);
    this.dirtyWalls.add(wallId);
  }

  /**
   * Retrieve simplified wall geometry.
   */
  getSimplifiedWallGeometry(wallId: string): SimplifiedWallGeometry | null {
    // If cache is disabled, always return null to force recalculation
    if (isCacheDisabled()) {
      return null;
    }
    const geometry = this.simplifiedGeometryCache.get(wallId);
    if (!geometry) {
      return null;
    }
    // Return a deep clone to prevent external modifications
    return {
      ...geometry,
      start: geometry.start.clone(),
      end: geometry.end.clone(),
      dependencyVersions: new Map(geometry.dependencyVersions),
      edgeConnectionVersions: geometry.edgeConnectionVersions ? new Map(geometry.edgeConnectionVersions) : undefined
    };
  }

  /**
   * Cache joint polygon data.
   */
  setJointPolygon(jointId: string, polygonData: JointPolygonData, wallIds: string[]): void {
    // If cache is disabled, don't store anything
    if (isCacheDisabled()) {
      return;
    }
    // Capture actual wall versions and compute topology hash
    const wallVersions = new Map<string, number>();
    for (const wallId of wallIds) {
      const wall = getFloorplannerStore().wallsMap.get(wallId);
      if (wall) {
        wallVersions.set(wallId, wall.geometryVersion || 0);
      } else {
        wallVersions.set(wallId, 0);
      }
    }

    // Compute topology hash for quick staleness detection
    const topologyHash = `${wallIds.sort().join(',')}:${Array.from(wallVersions.values()).join(',')}`;

    const cached: CachedJointPolygon = {
      jointId,
      polygonData,
      wallIds: [...wallIds],
      wallVersions,
      topologyHash,
      lastCalculated: Date.now()
    };

    this.jointPolygonCache.set(jointId, cached);

    // Register joint for batch invalidation
    this.registerJoint(jointId, wallIds);
  }

  /**
   * Retrieve cached joint polygon.
   */
  getJointPolygon(jointId: string, floorplannerStore?: any): CachedJointPolygon | null {
    // If cache is disabled, always return null to force recalculation
    if (isCacheDisabled()) {
      return null;
    }
    const cached = this.jointPolygonCache.get(jointId);
    if (!cached) {
      return null;
    }

    // Validate against participating walls' geometry versions
    if (floorplannerStore && cached.wallIds) {
      // First, check topology hash for quick staleness detection
      const currentWallIds = cached.wallIds.filter(id => getFloorplannerStore().wallsMap.has(id));

      // If walls have been added or removed, topology has changed
      if (currentWallIds.length !== cached.wallIds.length) {
        return null;
      }

      // Compute current topology hash
      const wallVersions = new Map<string, number>();
      for (const wallId of cached.wallIds) {
        const wall = getFloorplannerStore().wallsMap.get(wallId);
        if (!wall) {
          return null; // Wall no longer exists
        }
        wallVersions.set(wallId, wall.geometryVersion || 0);
      }
      const currentTopologyHash = `${cached.wallIds.sort().join(',')}:${Array.from(wallVersions.values()).join(',')}`;

      // Compare with cached topology hash
      if (currentTopologyHash !== cached.topologyHash) {
        return null; // Topology has changed
      }

      // Detailed per-wall version check
      for (const wallId of cached.wallIds) {
        const wall = getFloorplannerStore().wallsMap.get(wallId);
        if (!wall) {
          return null; // Wall no longer exists
        }

        const currentVersion = wall.geometryVersion || 0;
        const cachedVersion = cached.wallVersions.get(wallId) || 0;
        if (currentVersion !== cachedVersion) {
          return null; // Stale cache entry
        }
      }
    }

    return cached;
  }

  /**
   * Cache outline segments.
   */
  setOutlineSegments(wallId: string, segments: OutlineSegmentCollection): void {
    // If cache is disabled, don't store anything
    if (isCacheDisabled()) {
      return;
    }
    const cached: CachedOutlineSegments = {
      wallId,
      segments,
      geometryVersion: segments.geometryVersion,
      lastCalculated: Date.now()
    };

    this.outlineSegmentCache.set(wallId, cached);
  }

  /**
   * Retrieve cached outline segments.
   */
  getOutlineSegments(wallId: string): CachedOutlineSegments | null {
    // If cache is disabled, always return null to force recalculation
    if (isCacheDisabled()) {
      return null;
    }
    return this.outlineSegmentCache.get(wallId) || null;
  }

  getWallGeometryOrFallback(wallId: string): WallGeometry {
    // First, check if geometryCache has an entry for wallId
    const cached = this.geometryCache.get(wallId);
    if (cached) {
      return this.cloneWallGeometry(cached);
    }

    // If not found, query renderStore for the wall's geometry
    const inner = renderStore.innerWallShapesMap.get(wallId) || [];
    const outline1 = renderStore.outline1WallShapesMap.get(wallId) || [];
    const outline2 = renderStore.outline2WallShapesMap.get(wallId) || [];
    const endcaps = renderStore.endCapsWallShapesMap.get(wallId) || [];

    return {
      inner: this.cloneWallShapeArray(inner),
      outline1: this.cloneWallShapeArray(outline1),
      outline2: this.cloneWallShapeArray(outline2),
      endcaps: this.cloneWallShapeArray(endcaps)
    };
  }

  getInnerShapes(wallId: string): WallShapeType[] {
    const geometry = this.getWallGeometryOrFallback(wallId);
    return geometry.inner;
  }

  getOutline1Shapes(wallId: string): WallShapeType[] {
    const geometry = this.getWallGeometryOrFallback(wallId);
    return geometry.outline1;
  }

  getOutline2Shapes(wallId: string): WallShapeType[] {
    const geometry = this.getWallGeometryOrFallback(wallId);
    return geometry.outline2;
  }

  getEndcapShapes(wallId: string): WallShapeType[] {
    const geometry = this.getWallGeometryOrFallback(wallId);
    return geometry.endcaps;
  }

  hasWallGeometry(wallId: string): boolean {
    // If cache is disabled, always return false
    if (isCacheDisabled()) {
      return false;
    }
    return this.geometryCache.has(wallId);
  }

  /**
   * Check if cached geometry is still valid for a wall.
   * Geometry is invalid if:
   * 1. The wall's geometry version has changed
   * 2. Any connected wall's geometry version has changed
   */
  isCachedGeometryValid(wallId: string, wall: any, floorplannerStore: any): boolean {
    // If cache is disabled, always return false to force recalculation
    if (isCacheDisabled()) {
      return false;
    }
    // Always use simplified validation with endpoint-based checks
    return this.isSimplifiedGeometryValid(wallId, wall, floorplannerStore);
  }

  /**
   * Validate simplified geometry cache.
   * Checks endpoint positions and edge connections.
   */
  private isSimplifiedGeometryValid(wallId: string, wall: any, floorplannerStore: any): boolean {
    const cached = this.simplifiedGeometryCache.get(wallId);
    if (!cached) {
      return false;
    }

    // Check if wall's own geometry version has changed
    const currentVersion = wall.geometryVersion || 0;
    if (currentVersion !== cached.geometryVersion) {
      return false;
    }

    // Check if endpoint positions have changed (use epsilon-based comparison)
    const EPSILON = 1e-8;
    if (wall.start.distanceToSquared(cached.start) > EPSILON || wall.end.distanceToSquared(cached.end) > EPSILON) {
      return false;
    }

    // Check if width has changed
    if ((wall.wallWidth || 0) !== cached.width) {
      return false;
    }

    // Check if edge connections have been added or removed
    // Compare the set of current edge connection composite keys against cached versions
    // Composite key format: ${targetWallId}:${sourceEndpoint}:${targetEndpoint}
    const currentEdgeConnKeys = new Set<string>(
      (wall.edgeConnections || []).map((conn: any) => `${conn.targetWallId}:${conn.sourceEndpoint}:${conn.targetEndpoint}` as string)
    );
    const cachedEdgeConnKeys = new Set<string>(
      (cached.edgeConnectionVersions ? Array.from(cached.edgeConnectionVersions.keys()) : []) as string[]
    );

    // If the sets differ in size or members, the cache is invalid
    if (currentEdgeConnKeys.size !== cachedEdgeConnKeys.size) {
      return false;
    }

    for (const connKey of currentEdgeConnKeys) {
      if (!cachedEdgeConnKeys.has(connKey)) {
        return false;
      }
    }

    // Check if any edge-connected wall's version has changed (for existing connections)
    // Use composite key for precise invalidation
    if (wall.edgeConnections && cached.edgeConnectionVersions) {
      for (const edgeConn of wall.edgeConnections) {
        const connectedWall = getFloorplannerStore().wallsMap.get(edgeConn.targetWallId);
        if (connectedWall) {
          const connectedVersion = connectedWall.geometryVersion || 0;
          const compositeKey = `${edgeConn.targetWallId}:${edgeConn.sourceEndpoint}:${edgeConn.targetEndpoint}`;
          const cachedConnectedVersion = cached.edgeConnectionVersions.get(compositeKey) || 0;
          if (connectedVersion !== cachedConnectedVersion) {
            return false;
          }
        }
      }
    }

    return true;
  }

  registerJoint(jointId: string, wallIds: string[]): void {
    // Create or get the Set for this joint in jointToWallsMap
    let wallSet = this.jointToWallsMap.get(jointId);
    if (!wallSet) {
      wallSet = new Set();
      this.jointToWallsMap.set(jointId, wallSet);
    }

    // Add all wallIds to the set
    wallIds.forEach(wallId => wallSet!.add(wallId));
  }

  registerJointFromInfo(joint: JointInfo): void {
    // Extract wall IDs from joint.connectedWalls
    const wallIds = joint.connectedWalls.map(w => w.wallId);
    this.registerJoint(joint.id, wallIds);
  }

  clearJoint(jointId: string): void {
    // Always delete the polygon entry, even if the joint was not registered in
    // jointToWallsMap. Runtime invalidation can hit that path for newly-created
    // topology before the joint has been registered for coordinated processing.
    this.jointPolygonCache.delete(jointId);

    // Get the Set of wall IDs from jointToWallsMap for this joint
    const wallIds = this.jointToWallsMap.get(jointId);
    if (!wallIds) {
      return;
    }

    // For each wall ID in the set, delete from geometryCache and dirtyWalls
    wallIds.forEach(wallId => {
      this.geometryCache.delete(wallId);
      this.dirtyWalls.delete(wallId);
    });

    // Delete the joint from jointToWallsMap
    this.jointToWallsMap.delete(jointId);
  }

  clearAllJointPolygons(): void {
    this.jointPolygonCache.clear();
  }

  clearWall(wallId: string): void {
    this.geometryCache.delete(wallId);
    this.dirtyWalls.delete(wallId);
    // Clear from new cache types
    this.simplifiedGeometryCache.delete(wallId);
    this.outlineSegmentCache.delete(wallId);

    // Clear joint polygons that reference this wall
    this.clearJointPolygonsForWall(wallId);
  }

  /**
   * Clear joint polygon cache entries for a specific wall.
   * Iterates through jointPolygonCache and deletes entries whose wallIds contains the specified wallId.
   */
  clearJointPolygonsForWall(wallId: string): void {
    const jointsToDelete: string[] = [];

    this.jointPolygonCache.forEach((cached, jointId) => {
      if (cached.wallIds.includes(wallId)) {
        jointsToDelete.push(jointId);
      }
    });

    jointsToDelete.forEach(jointId => {
      this.jointPolygonCache.delete(jointId);
    });
  }

  /**
   * Clear outline segment cache for a specific wall.
   */
  clearOutlineSegmentsForWall(wallId: string): void {
    this.outlineSegmentCache.delete(wallId);
  }

  /**
   * Clear simplified geometry cache for a specific wall.
   */
  clearSimplifiedGeometry(wallId: string): void {
    this.simplifiedGeometryCache.delete(wallId);
  }

  clearAll(): void {
    this.geometryCache.clear();
    this.jointToWallsMap.clear();
    this.dirtyWalls.clear();
    // Clear all new cache types
    this.simplifiedGeometryCache.clear();
    this.jointPolygonCache.clear();
    this.outlineSegmentCache.clear();
  }

  commitToRenderStore(): void {
    let committedCount = 0;

    // Commit legacy geometry cache
    this.geometryCache.forEach((geometry, wallId) => {
      renderStore.innerWallShapesMap.set(wallId, geometry.inner);
      renderStore.outline1WallShapesMap.set(wallId, geometry.outline1);
      renderStore.outline2WallShapesMap.set(wallId, geometry.outline2);
      renderStore.endCapsWallShapesMap.set(wallId, geometry.endcaps);
      this.markWallCommitted(wallId, false); // Clear dirty flag but keep cached geometry
      committedCount++;
    });

    // Commit joint polygons to renderStore
    this.jointPolygonCache.forEach((cached, jointId) => {
      renderStore.jointPolygonsMap.set(jointId, cached.polygonData);
    });

    // Commit outline segments to renderStore
    this.outlineSegmentCache.forEach((cached, wallId) => {
      renderStore.outlineSegmentsMap.set(wallId, cached.segments);
    });
  }

  commitWallToRenderStore(wallId: string): void {
    const geometry = this.geometryCache.get(wallId);
    if (!geometry) {
      return;
    }

    renderStore.innerWallShapesMap.set(wallId, geometry.inner);
    renderStore.outline1WallShapesMap.set(wallId, geometry.outline1);
    renderStore.outline2WallShapesMap.set(wallId, geometry.outline2);
    renderStore.endCapsWallShapesMap.set(wallId, geometry.endcaps);

    // Also commit outline segments for this wall if they exist
    const outlineSegments = this.outlineSegmentCache.get(wallId);
    if (outlineSegments) {
      renderStore.outlineSegmentsMap.set(wallId, outlineSegments.segments);
    }

    // Commit joint polygons that involve this wall
    const wall = getFloorplannerStore().wallsMap.get(wallId);
    if (wall && wall.edgeConnections) {
      // Use a Set to deduplicate jointIds
      const jointIds = new Set<string>();

      // For each edge connection, compute the jointId
      wall.edgeConnections.forEach(edgeConn => {
        const wallIds = [wallId, edgeConn.targetWallId].sort();
        const jointId = `joint_${wallIds[0]}_${wallIds[1]}_${edgeConn.sourceEndpoint}_${edgeConn.targetEndpoint}`;
        jointIds.add(jointId);
      });

      // Commit each unique joint polygon
      jointIds.forEach(jointId => {
        const cached = this.jointPolygonCache.get(jointId);
        if (cached) {
          renderStore.jointPolygonsMap.set(jointId, cached.polygonData);
        }
      });
    }

    this.markWallCommitted(wallId, false); // Clear dirty flag but keep cached geometry
  }

  getDirtyWalls(): Set<string> {
    return new Set(this.dirtyWalls);
  }

  isDirty(wallId: string): boolean {
    return this.dirtyWalls.has(wallId);
  }

  getStats(): {
    cachedWallsCount: number;
    dirtyWallsCount: number;
    jointsTracked: number;
    totalWallsInJoints: number;
    simplifiedGeometryCachedCount: number;
    jointPolygonsCachedCount: number;
    outlineSegmentsCachedCount: number;
  } {
    // Calculate totalWallsInJoints
    let totalWallsInJoints = 0;
    this.jointToWallsMap.forEach(wallSet => {
      totalWallsInJoints += wallSet.size;
    });

    return {
      cachedWallsCount: this.geometryCache.size,
      dirtyWallsCount: this.dirtyWalls.size,
      jointsTracked: this.jointToWallsMap.size,
      totalWallsInJoints,
      simplifiedGeometryCachedCount: this.simplifiedGeometryCache.size,
      jointPolygonsCachedCount: this.jointPolygonCache.size,
      outlineSegmentsCachedCount: this.outlineSegmentCache.size
    };
  }

  /**
   * Clear all cached geometry.
   * Call this on application initialization to prevent stale cache from refresh.
   */
  clearAllCache(): void {
    this.geometryCache.clear();
    this.simplifiedGeometryCache.clear();
    this.jointPolygonCache.clear();
    this.outlineSegmentCache.clear();
    this.jointToWallsMap.clear();
    this.dirtyWalls.clear();
  }

  private cloneWallGeometry(geometry: WallGeometry): WallGeometry {
    return {
      inner: this.cloneWallShapeArray(geometry.inner),
      outline1: this.cloneWallShapeArray(geometry.outline1),
      outline2: this.cloneWallShapeArray(geometry.outline2),
      endcaps: this.cloneWallShapeArray(geometry.endcaps)
    };
  }

  markWallCommitted(wallId: string, clearCachedGeometry: boolean = false): void {
    // Remove wall from dirty flags
    this.dirtyWalls.delete(wallId);

    // Optionally clear cached geometry
    if (clearCachedGeometry) {
      this.geometryCache.delete(wallId);
    }
  }

  private cloneWallShapeArray(shapes: WallShapeType[]): WallShapeType[] {
    return shapes.map(shape => {
      // Simplified cloning - only clone paths, omit corners
      // Corner metadata is not needed for 4-point rectangle architecture
      return {
        wall: shape.wall, // Walls themselves are immutable for the cache scope
        paths: shape.paths.map(vector => vector.clone()),
        lineSide: shape.lineSide,
        legacyCorners: shape.legacyCorners ?? true // Use nullish coalescing for backward compatibility
      };
    });
  }
}

// Export singleton instance
export const wallGeometryCache = new WallGeometryCache();
